import type { FocusEventHandler, KeyboardEventHandler } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { selectWithRetry, useSelectOnFocusEnabled } from '@makemydeal/dr-shared-components/select-on-focus';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as offerReduxActionCreators from '@makemydeal/dr-dash-store/actions/offerRedux';
import * as offerActionTypes from '@makemydeal/dr-dash-store/actionTypes/offerActionTypes';
import * as compositeSelectors from '@makemydeal/dr-dash-store/selectors/compositeSelectors';
import * as offerActionCreators from '@makemydeal/dr-dash-store/actions/offerActionCreators';
import * as interactionAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/interactionAnalyticsActionCreators';
import * as dealScreenErrorActionCreators from '@makemydeal/dr-dash-store/actions/dealScreenErrorActionCreators';
import { dealLockHooks, useTermNotSupported } from '../../../hooks';

import { GENERIC_LAST_FIELD_CHANGED_ERROR, LEASE_TERM, TERM } from '../../../constants';
import { parseSelectedTermMonths, addValueSuffix, getTermNotSupportedMessage, removeValueSuffix } from '../utils';
import { LEASE } from '@makemydeal/dr-platform-types';
import type { StateTree } from '@makemydeal/dr-dash-types';
import { DealTabs } from '@makemydeal/dr-shared-types';
import { kebabCase } from 'lodash';

const LEASE_TERM_OVERRIDE_INDICATOR_TEST_ID = 'lease-term-override-indicator';

export const useLeaseTermField = ({ isSummaryTab }: { isSummaryTab?: boolean }) => {
    const dispatch = useDispatch();

    const failed = useSelector(compositeSelectors.getLastFailureActionIfError(offerActionTypes.SELECTED_TERM));
    const isDealScreenRedDotErrorsEnabled = useSelector(featureToggleSelectors.isDealScreenRedDotErrorsEnabled);
    const isAutoSelectEnabled = useSelectOnFocusEnabled();
    const isDealCentralExperience = useSelector(dealerSelectors.isDealCentralExperience);
    const selectedTermId = useSelector(offerReduxSelectors.getTermIdByOfferType);
    const offerType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const isPaymentGridEnabled = useSelector((state: StateTree) => offerReduxSelectors.getPaymentGridToggle(state, offerType));
    const isFocused = useRef(false);
    const isLocked = dealLockHooks.useIsDealLocked();
    const isImprovedAutoSelectEnabled = useSelector(featureToggleSelectors.isImprovedAutoSelectEnabled);
    const isExplicitLenderP1Enabled = useSelector(featureToggleSelectors.isExplicitLenderP1Enabled);
    const isTermFieldDownPaymentTermIdEnabled = useSelector(featureToggleSelectors.isTermFieldDownPaymentTermIdEnabled);
    const downPayment = useSelector((state: StateTree) => offerReduxSelectors.getDownPaymentByOfferType(state, LEASE)) ?? 0;
    const isTermNotSupported = useTermNotSupported();

    const formattedTerm = useMemo((): string => {
        const months = parseSelectedTermMonths(selectedTermId);
        return addValueSuffix(months);
    }, [selectedTermId]);

    const [termNumber, setTermNumber] = useState(formattedTerm);

    // Redux holds the term the user asked for, because dash does not revert a failed SELECTED_TERM. It does
    // not hold the term that was replaced, so this field stores that, tagged with the term it applies to.
    // Hide the generic error only when the indicator shows this message in its place.
    const [replacedTerm, setReplacedTerm] = useState<{ forTermMonths: number; previousMonths?: number }>();
    const currentTermMonths = parseSelectedTermMonths(selectedTermId);
    const previousTermMonths =
        replacedTerm && replacedTerm.forTermMonths === currentTermMonths ? replacedTerm.previousMonths : undefined;
    const termNotSupportedMessage = isTermNotSupported ? getTermNotSupportedMessage(currentTermMonths, previousTermMonths) : '';

    const hasBlockingFailure = failed && !termNotSupportedMessage;
    const failedMessage = hasBlockingFailure ? GENERIC_LAST_FIELD_CHANGED_ERROR : undefined;

    useEffect(() => {
        if (termNumber !== formattedTerm) {
            setTermNumber(formattedTerm);
        }
    }, [formattedTerm]);

    // A term with no matching id (including 0) never gets saved, so `formattedTerm` never changes and the
    // effect above won't run — leaving the typed value on screen next to the old term's payment. Watching
    // `termNumber` puts the real term back, same as FinanceTermField already does.
    useEffect(() => {
        if (isImprovedAutoSelectEnabled && !isFocused.current && termNumber !== formattedTerm) {
            setTermNumber(formattedTerm);
        }
    }, [isImprovedAutoSelectEnabled, formattedTerm, termNumber]);

    useEffect(() => {
        if (isDealCentralExperience) {
            if (isFocused.current) {
                setTermNumber(removeValueSuffix(formattedTerm));
            } else {
                setTermNumber(formattedTerm);
            }
        }
    }, [selectedTermId]);

    useEffect(() => {
        if (isDealScreenRedDotErrorsEnabled && isSummaryTab) {
            dispatch(dealScreenErrorActionCreators.setFieldError(DealTabs.SUMMARY, kebabCase(LEASE_TERM), hasBlockingFailure));
        }
    }, [isDealScreenRedDotErrorsEnabled, dispatch, hasBlockingFailure, isSummaryTab]);

    const updateSelectedTerm = (termId: string) =>
        dispatch(offerReduxActionCreators.selectedTerm(termId, { suppressAnalytics: true, payment: true }));

    const handleInputChanged = (e: any) => {
        const newValue = e.target.value;
        const newValueNumber = Number(e.target.value);
        const prevTerm = parseSelectedTermMonths(selectedTermId);
        if (newValue === termNumber || newValue.length > 3 || isNaN(newValueNumber)) {
            return;
        }

        dispatch(offerActionCreators.updatePaymentCallPendingState(prevTerm !== newValueNumber));

        setTermNumber(newValueNumber.toString());
    };

    const handleInputFocus: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = (e) => {
        isFocused.current = true;
        const target = e.currentTarget;
        const stripped = removeValueSuffix(termNumber);
        setTermNumber(stripped);
        if (isAutoSelectEnabled) selectWithRetry(target, stripped);
    };

    const validateAndUpdateTerm = () => {
        const term = parseSelectedTermMonths(selectedTermId);
        const termValue = Number(termNumber.replace('Months', '').trim());
        if (term !== termValue) {
            const id = isTermFieldDownPaymentTermIdEnabled
                ? offerReduxSelectors.getTermId(LEASE, termValue, { DOWNPAYMENT: downPayment })
                : offerReduxSelectors.getTermId(LEASE, termValue);
            if (id) {
                // After a failure redux holds the failed term. Do not record it as the previous value.
                if (isImprovedAutoSelectEnabled && !isTermNotSupported) {
                    setReplacedTerm({ forTermMonths: termValue, previousMonths: term });
                }
                updateSelectedTerm(id);
            }
            dispatch(interactionAnalyticsActionCreators.leaseTermDropdown());
        }

        setTermNumber(addValueSuffix(termValue));
    };

    const handleInputBlur = () => {
        isFocused.current = false;
        validateAndUpdateTerm();
    };

    const handleKeyDown: KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement> = (e) => {
        if (e.key === 'Enter') {
            validateAndUpdateTerm();
        }
    };

    return {
        label: isSummaryTab ? `${TERM} (Monthly)` : LEASE_TERM,
        overrideIndicatorProps: {
            enabled: isImprovedAutoSelectEnabled,
            show: Boolean(termNotSupportedMessage),
            message: termNotSupportedMessage,
            testId: LEASE_TERM_OVERRIDE_INDICATOR_TEST_ID,
            announce: isExplicitLenderP1Enabled
        },
        inputProps: {
            'data-testid': 'CMDTermInput',
            name: LEASE_TERM,
            hasError: hasBlockingFailure,
            errorMessage: failedMessage,
            onChange: handleInputChanged,
            onFocus: handleInputFocus,
            onBlur: handleInputBlur,
            value: termNumber,
            size: 'sm' as const,
            onKeyDown: handleKeyDown,
            readOnly: isPaymentGridEnabled,
            disabled: isLocked
        }
    };
};
